import toolbox from "@iap/kit-util";
import LogUtil from "@/betby/utils/LogUtil";
import { systemApi } from "@/betby/api/services";
import {
  TRACK_EVENT_SDK_START_INTERFACE_SPM,
  TRACK_EVENT_SDK_START_INTERFACE_TIME_SPM,
} from "@/betby/utils/TrackEvents";
import analyticsBridge from "@/betby/adapters/analytics/analyticsBridge";

/**
 * 时间管理器
 * 用于同步服务器时间，并修正 kit-http 的时间戳生成逻辑
 */
class TimeManager {
  private static instance: TimeManager;
  private offset: number = 0;
  private isSynced: boolean = false;

  private constructor() {
    this.patchToolbox();
  }

  public static getInstance(): TimeManager {
    if (!TimeManager.instance) {
      TimeManager.instance = new TimeManager();
    }
    return TimeManager.instance;
  }

  /**
   * 修正 toolbox.localTimeStampMill
   * 让 kit-http 使用服务器时间
   */
  private patchToolbox() {
    if (toolbox) {
      try {
        Object.defineProperty(toolbox, "localTimeStampMill", {
          get: () => {
            return this.getCurrentTimestamp();
          },
          configurable: true,
        });
        LogUtil.info(
          "[TimeManager] Successfully hooked toolbox.localTimeStampMill",
        );
      } catch (e) {
        LogUtil.error(
          "[TimeManager] Failed to hook toolbox.localTimeStampMill",
          e,
        );
      }
    }
  }

  /**
   * 获取当前修正后的服务器时间戳
   */
  public getCurrentTimestamp(): number {
    const timestamp = Date.now() + this.offset;
    LogUtil.info("[TimeManager] getCurrentTimestamp", timestamp);
    return timestamp;
  }

  /**
   * 同步服务器时间
   * @param serverTimeStr 服务器时间字符串 (ISO 8601 或其他格式)
   */
  public sync(serverTimeStr: string) {
    if (!serverTimeStr) return;

    try {
      // 解析服务器时间
      // 注意：如果 serverTimeStr 是 ISO 字符串，Date.parse 会自动处理时区
      // 如果不是 ISO 格式，可能需要手动处理
      const serverTime = new Date(serverTimeStr).getTime();

      if (isNaN(serverTime)) {
        LogUtil.warn(
          "[TimeManager] Invalid server time format:",
          serverTimeStr,
        );
        return;
      }

      const localTime = Date.now();

      // 计算偏移量 = 服务器时间 - 本地时间
      // 考虑到网络延迟，实际偏移量可能需要减去 RTT/2，但这里简化处理
      this.offset = serverTime - localTime;
      this.isSynced = true;

      LogUtil.info(
        `[TimeManager] Time synced. Offset: ${this.offset}ms. Current Server Time: ${new Date(this.getCurrentTimestamp()).toLocaleString()}`,
      );
    } catch (error) {
      LogUtil.error("[TimeManager] Failed to sync time:", error);
    }
  }

  /**
   * 初始化并从服务器获取时间配置
   * 启动页请求，打 s_start_interface_spm / s_start_interface_time_spm 埋点
   */
  public async initSystemTime() {
    const requestStartTime = Date.now();
    let status: "suc" | "fail" = "fail";
    try {
      // 避免首次请求使用了尚未同步的时间导致问题（通常首次请求本身就是为了同步，所以没关系）
      // 且 systemApi.getConfig 应该是不加密或者使用默认时间戳也没关系的接口
      const response = await systemApi.getConfig({
        skipErrorHandler: true, // 避免报错干扰
      });

      if (response.code === 200 && response.data?.system_time) {
        this.sync(response.data.system_time);
        status = "suc";
      } else {
        LogUtil.warn("[TimeManager] System config response invalid:", response);
      }
    } catch (error) {
      // 仅记录警告，不阻断流程
      LogUtil.warn("[TimeManager] Failed to fetch system config:", error);
    } finally {
      try {
        analyticsBridge.track(TRACK_EVENT_SDK_START_INTERFACE_SPM, {
          src: 516,
          eb_key1: "/user/basic/system/config",
        });

        const loadDurationMs = Math.max(Date.now() - requestStartTime, 0);
        analyticsBridge.track(TRACK_EVENT_SDK_START_INTERFACE_TIME_SPM, {
          src: 516,
          eb_key1: "/user/basic/system/config",
          eb_key2: status,
          time: loadDurationMs,
        });
      } catch (e) {
        LogUtil.error(
          "[TimeManager] Failed to track system config interface time",
          e,
        );
      }
    }
  }
}

export default TimeManager.getInstance();
